import {
  apiv2_getCategoryInterval,
  apiv2_getFirmCustomLists,
  apiv2_getFirmInterval,
  apiv2_getFundCustomLists,
  apiv2_getFundInterval,
  getCustomListMetrics,
} from "@/app/api2"
import {
  INITIAL_CHART,
  INITIAL_CHART_RANKING,
  INITIAL_COMPARISON,
  INITIAL_ENTITY,
  INITIAL_TERM,
} from "@/app/global_data"
import { mainLogger as log } from "@/app/logger"
import { AppData } from "@/app/providers/app_data.class"
import { LocalStorage, SessionStorage } from "@/app/storage"
import {
  AllValues,
  Chart,
  ComparisonNew,
  Entity,
  EntityValues,
  EventTracker,
  Filter,
  Interval,
  Term,
  findChart,
} from "@/app/types"
import { isFunction, isNotNull, isNull } from "@/app/utils"

export class GlobalData {
  #id
  // these are global data initialized when the app is loaded
  #entity: Entity
  #term: Term
  #comparison: ComparisonNew
  #chart: Chart
  // this are API metrics for the selected interval
  #categoryValues: AllValues | null // null = isLoading (for funds)
  #entityValues: EntityValues | null // null = isLoading
  // notify is invoked if global value is updated
  #notifyFn: (data: GlobalData) => void
  #error: {
    hasError: boolean
    message: string
  }
  #filter: Filter.Type

  constructor(from: GlobalData | null = null) {
    this.#id = Math.random()
    this.#error = { hasError: false, message: "" }
    this.#filter = SessionStorage.get("filter") ?? {
      key: Filter.DEFAULT.key,
      name: Filter.DEFAULT.name,
      description: Filter.DEFAULT.description,
      mf: true,
      etf: true,
      active: true,
      index: true,
      fof: false,
      not_fof: true,
    }

    if (from) {
      // clone from the passed GlobalData
      this.#entity = from.#entity
      this.#term = from.#term
      this.#comparison = from.#comparison
      this.#chart = from.#chart
      this.#entityValues = from.#entityValues
      this.#categoryValues = from.#categoryValues
      this.#notifyFn = from.#notifyFn
      this.#error = from.#error
      this.#filter = from.#filter
    }
  }

  toString() {
    return (
      `GlobalData loaded:${this.isLoaded()} with: ` +
      `entity: ${this.getEntity()?.toString()}, term: ${this.getTerm()?.toString()}, ` +
      `comparison: ${this.getComparison()?.toString()}, chart: ${this.getChart()?.toString()} with notifyFn ${this.#notifyFn}`
    )
  }

  globalDataChangeToString() {
    return (
      `GlobalData changed with: ` +
      `entity: ${this.getEntity()?.toString()}, term: ${this.getTerm()?.toString()}, ` +
      `comparison: ${this.getComparison()?.toString()}, chart: ${this.getChart()?.toString()} `
    )
  }

  id() {
    return this.#id
  }

  setNotify(notifyFn: (data: GlobalData) => void) {
    this.#notifyFn = notifyFn
  }

  isLoaded(): boolean {
    return (
      isNotNull(this.getEntity()) &&
      isNotNull(this.getTerm()) &&
      isNotNull(this.getComparison()) &&
      isNotNull(this.getChart()) &&
      isNotNull(this.getEntityValues()) &&
      (this.getEntity().isFirm() || isNotNull(this.getAllValues())) &&
      (!this.#comparison.isCustomList() || this.hasCustomMetrics())
    )
  }

  isLoading(): boolean {
    return !this.isLoaded()
  }

  hasCustomMetrics(): boolean {
    const customMetrics =
      this.getEntity().isFirm() ?
        this.#entityValues?.firmsets?.customMetrics
      : this.#entityValues?.fundsets?.customMetrics
    return isNotNull(customMetrics)
  }

  getEntity() {
    return this.#entity
  }

  getTerm() {
    return this.#term
  }

  getComparison() {
    return this.#comparison
  }

  getChart() {
    return this.#chart
  }

  getEntityValues() {
    return this.#entityValues
  }

  getAllValues() {
    // if we dont have a fund/firm, then we cant return values
    if (isNull(this.getEntity())) return null

    // if we are comparing with a fund, then return the category values
    if (this.getEntity().isFund()) return this.#categoryValues

    // if dont have industry metrics or we dont have an interval, then we can return industry values
    const appData = AppData.get()
    if (appData.isLoading() || isNull(this.getTerm())) return null

    return appData.industryMetrics.get(this.getTerm().interval)
  }

  getError() {
    if (this.#error.hasError) return this.#error
    else return null
  }

  // return all values as an object so it can be destructured easily
  get() {
    const id = this.id()
    const entity = this.getEntity()
    const term = this.getTerm()
    const comparison = this.getComparison()
    const chart = this.getChart()
    const entityValues = this.getEntityValues()
    const allValues = this.getAllValues()
    const isLoaded = this.isLoaded()
    const filter = this.getFilter()
    return {
      id,
      isLoaded,
      entity,
      term,
      comparison,
      chart,
      entityValues,
      allValues,
      filter,
    }
  }

  async refresh() {
    const appData = AppData.get()

    // create prev global data before initial refresh to avoid getting stuck if any error happens with userPreferences config
    if (SessionStorage.isAvailable()) {
      const defaultGlobalData = new GlobalData()

      const entity =
        Entity.isFundString(INITIAL_ENTITY.entityType) ?
          appData.lookup.getFund(INITIAL_ENTITY.id)
        : appData.lookup.getFirm(INITIAL_ENTITY.id)

      defaultGlobalData.#entity = new Entity(entity!)
      defaultGlobalData.#chart = INITIAL_CHART
      defaultGlobalData.#comparison = INITIAL_COMPARISON

      const term = appData.lookup.getTerm(
        defaultGlobalData.#chart.isPeriodic(),
        INITIAL_TERM.interval,
        INITIAL_TERM.key
      )

      defaultGlobalData.#term = term!

      SessionStorage.set("prevGlobalData", defaultGlobalData.#toJson())
    }

    // we need to make sure app data is loaded  before refreshing global data
    if (appData.isLoading()) return

    // first we attempt to load from session storage
    const sessionStorageData = SessionStorage.get("globalData")
    const shouldLoadFromSessionStorage =
      isNotNull(sessionStorageData) &&
      Object.keys(sessionStorageData).length !== 0

    if (shouldLoadFromSessionStorage) {
      this.#fromJson(sessionStorageData, appData)
    }

    const userPreferencesData = LocalStorage.get("userPreferences")

    const shouldLoadFromUserPreferences =
      userPreferencesData && Object.keys(userPreferencesData).length !== 0

    // load from user preferences if not in session storage
    if (shouldLoadFromUserPreferences && !shouldLoadFromSessionStorage) {
      const entityType =
        typeof userPreferencesData.fundId === "string" ? "fund" : "firm"
      const entity =
        Entity.isFundString(entityType) ?
          appData.lookup.getFund(userPreferencesData.fundId!)
        : appData.lookup.getFirm(userPreferencesData.firmId!)

      if (!entity) return

      this.#entity = new Entity(entity)
      this.#chart = findChart(userPreferencesData.chartCode)

      this.#comparison = ComparisonNew.getComparisonParameters(
        userPreferencesData.comparisonGroup,
        userPreferencesData.calculation
      )

      const term = appData.lookup.getTerm(
        this.#chart.isPeriodic(),
        userPreferencesData.interval,
        userPreferencesData.termKey
      )

      if (!term) return
      this.#term = term
    }
    // last resort is system default
    if (!shouldLoadFromSessionStorage && !shouldLoadFromUserPreferences) {
      this.#loadDefaultSettings(appData)
    }

    log.debug(`Refreshed ${this}`, "initiating load entity/category values")

    await this.#loadEntityValues()
    await this.#loadCategoryValues()

    if (this.#comparison.isCustomList()) {
      await this.#loadCustomListMetrics(
        this.#comparison.getGroup(),
        this.#term.interval
      )
    }
  }

  setFilter(filter: Filter.Type) {
    const eventTracker = EventTracker.get()

    const appData = AppData.get()

    const clone = new GlobalData(this)

    clone.#filter = filter
    clone.#entity = null // trigger new initialization in provider

    SessionStorage.set("filter", filter)

    eventTracker.trackEvent({
      event: "chart:change_global_data",
      data: {
        oldData: this.globalDataChangeToString(),
        newData: clone.globalDataChangeToString(),
      },
    })

    this.#removeLocalStorageGlobalMetrics() // clean global metrics before loading app data
    this.#removeLocalStorageIndustryMetrics() // clean industry metrics before loading app data
    appData.reloadAppData() // reload app data to load industry metrics filtered

    this.#notifyFn(clone)
  }

  getFilter() {
    return this.#filter
  }

  // common method to notify watchers (e.g. React) that new data is available
  // should be used like this
  //  - globalData.setGroup(newGroup).setChart(newChart).setTerm(newTerm).notify()
  //  - globalData.setGroup(newGroup).notify()
  //  - globalData.notify() will NOT work since there are no .setXXX() calls made
  //  - globalData.setGroup() will NOT work since .notify() is missing
  notify() {
    if (isFunction(this.#notifyFn)) {
      log.debug(
        "Setting new Global Data to Session Storage and notifying change.."
      )
      SessionStorage.set("globalData", this.#toJson())
      log.debug(`Notifying with new GlobalData:${this}`) // use ${this} so that we use .toString()
      this.#notifyFn(this)
    }
  }

  setEntity(newEntity: Entity): GlobalData {
    const eventTracker = EventTracker.get()

    if (isNull(newEntity) || this.#entity.equals(newEntity)) return this // nothing to do

    // set previous Global Data to LS in case an error happens
    SessionStorage.set("prevGlobalData", this.#toJson())
    this.#removeLocalStorageGlobalMetrics() // removes keys from previous entities

    const clone = new GlobalData(this)

    this.#updateEntity(clone, newEntity)

    eventTracker.trackEvent({
      event: "chart:change_global_data",
      data: {
        oldData: this.globalDataChangeToString(),
        newData: clone.globalDataChangeToString(),
      },
    })

    return clone
  }

  setFundFirm(isFund: boolean, id: string): GlobalData {
    const appData = AppData.get()
    if (appData.isLoading()) {
      log.error(
        "Could not setFundFirm(",
        { isFund, id },
        ") because app data is not loaded"
      )
      return this
    }

    if (isNull(id)) {
      log.debug("Missing id in setFundFirm:", { isFund, id })
      return this
    }

    const entityFundFirm =
      isFund ? appData.lookup.getFund(id) : appData.lookup.getFirm(id)

    log.info("Changing to entity:", { isFund, id, entityFundFirm })
    return this.setEntity(new Entity(entityFundFirm!)) // create a new object to trigger a re-paint
  }

  setFund(id: string): GlobalData {
    return this.setFundFirm(true, id)
  }

  setFirm(id: string): GlobalData {
    return this.setFundFirm(false, id)
  }

  setTerm(newTerm: Term): GlobalData {
    const eventTracker = EventTracker.get()

    SessionStorage.set("prevGlobalData", this.#toJson())
    if (
      isNull(newTerm) ||
      (this.#term.isPeriodic == newTerm.isPeriodic &&
        this.#term.interval == newTerm.interval &&
        this.#term.key == newTerm.key)
    )
      return this // nothing to do

    const clone = new GlobalData(this)
    this.#updateTerm(clone, newTerm)

    eventTracker.trackEvent({
      event: "chart:change_global_data",
      data: {
        oldData: this.globalDataChangeToString(),
        newData: clone.globalDataChangeToString(),
      },
    })

    return clone
  }

  setGroup(newGroup: string): GlobalData {
    SessionStorage.set("prevGlobalData", this.#toJson())

    if (isNull(newGroup) || this.#comparison.getGroup() == newGroup) return this // nothing to do

    const customListGroups = this.#comparison.getCustomGroups()

    const newComparison = ComparisonNew.getComparisonParameters(
      newGroup,
      this.#comparison.getCalculation(),
      customListGroups
    )

    return this.setComparison(newComparison)
  }

  setCalc(newCalc: string): GlobalData {
    SessionStorage.set("prevGlobalData", this.#toJson())

    if (isNull(newCalc) || this.#comparison.getCalculation() == newCalc)
      return this // nothing to do

    const customListGroups = this.#comparison.getCustomGroups()

    const newComparison = ComparisonNew.getComparisonParameters(
      this.#comparison.getGroup(),
      newCalc,
      customListGroups
    )

    return this.setComparison(newComparison)
  }

  setComparison(newComparison: ComparisonNew): GlobalData {
    const eventTracker = EventTracker.get()
    SessionStorage.set("prevGlobalData", this.#toJson())

    if (isNull(newComparison) || this.#comparison.equals(newComparison))
      return this // nothing to do

    const clone = new GlobalData(this)
    this.#updateComparison(clone, newComparison)

    eventTracker.trackEvent({
      event: "chart:change_global_data",
      data: {
        oldData: this.globalDataChangeToString(),
        newData: clone.globalDataChangeToString(),
      },
    })

    return clone
  }

  setChart(newChart: Chart): GlobalData {
    SessionStorage.set("prevGlobalData", this.#toJson())
    console.log("Switching to new chart", newChart)
    if (isNull(newChart) || this.#chart.code == newChart.code) return this // nothing to do
    const clone = new GlobalData(this)
    this.#updateChart(clone, newChart)
    return clone
  }

  // private updateXXX methods that handle dependencies between entity > term > chart changes
  // these update the "clone" GlobalData

  // when entity changes, we need to
  // - if current chart is not applicable to newEntity, then #updateChart() which may also #updateTerm()
  // - load entity/category API data for the currently selected interval (async)
  // - no change required for term, comparison
  #updateEntity(clone: GlobalData, newEntity: Entity) {
    clone.#entity = newEntity

    const isCustomListSelected = clone.#comparison.isCustomList()
    const hasEntityTypeChanged =
      clone.#entity.propertyName() !== this.#entity.propertyName()
    const customListKey = clone.#comparison.getGroup()
    const customListGroups = clone.#comparison.getCustomGroups()
    const isCustomListGlobal = customListGroups.some(
      (list) => list.key === customListKey && list.isGlobal
    )

    // the current chart is not applicable to new entity, so change to initial chart
    if (!clone.#chart.isShown(newEntity.isFund())) {
      this.#updateChart(clone, INITIAL_CHART)
    }

    // we need to make sure that the comparison doesn't need to be updated to default peers
    if (
      isCustomListSelected &&
      this.#shouldResetComparisonGroup(hasEntityTypeChanged, isCustomListGlobal)
    ) {
      clone.#comparison.setGroup(ComparisonNew.PEERS)
    }

    // load the entity metrics for the clone
    clone.#loadEntityValues()

    log.debug("Loaded entity values", clone.#entityValues)
    // if the category happens to be the same, then reuse the category metrics
    if (newEntity.getFund()?.categoryId == this.#entity.getFund()?.categoryId) {
      clone.#categoryValues = this.#categoryValues
    } else {
      clone.#loadCategoryValues()
    }
  }

  #shouldResetComparisonGroup(
    hasEntityTypeChanged: boolean,
    isCustomListGlobal: boolean
  ): boolean {
    return hasEntityTypeChanged || !isCustomListGlobal
  }

  // when term changes, we need to
  // - if isPeriodic is changed and not applicable to current chart, then #updateChart()
  // - if interval is different, then load entity/category data for the new interval (async)
  // - no changes required for entity, comparison
  #updateTerm(clone: GlobalData, newTerm: Term) {
    clone.#term = newTerm

    if (clone.#chart.isPeriodic() !== newTerm.isPeriodic) {
      newTerm.isPeriodic ?
        this.#updateChart(clone, INITIAL_CHART)
      : this.#updateChart(clone, INITIAL_CHART_RANKING)
    }

    if (this.#term.interval != newTerm.interval) {
      // reload entity/category values with the new interval
      clone.#loadEntityValues()
      clone.#loadCategoryValues()
    }
  }

  // when chart changes, we need to
  // - updateTerm(), if the newChart.isPeriodic() is different than currentTerm.isPeriodic()
  // - no changes required for entity, comparison
  #updateChart(clone: GlobalData, newChart: Chart) {
    if (!newChart.isValidFor(clone.#entity)) {
      log.error(
        `Cannot change chart to:${newChart.code} for entity:(${clone.#entity.propertyName()} ${clone.#entity.getId()})`
      )
      return
    }

    console.log("Updating to new chart", newChart)

    const eventTracker = EventTracker.get()

    if (clone.#term.isPeriodic == newChart.isPeriodic()) {
      // changing from periodic to periodic, or ranking to ranking
      // so we dont need to change the term
      clone.#chart = newChart
    } else {
      // we need to change term, so ensure that AppData is loaded
      const appData = AppData.get()
      if (appData.isLoading()) {
        log.error("Cannot change chart because appData is not loaded")
        return
      }
      // get the term with the same interval/duration but other type
      let newTerm = appData.lookup.getTerm(
        newChart.isPeriodic(),
        clone.#term.interval,
        clone.#term.key
      )

      if (clone.#term.interval === Interval.Annual) {
        newTerm = appData.lookup.getTerm(
          newChart.isPeriodic(),
          clone.#term.interval,
          newChart.isPeriodic() ?
            appData.metaTerms.periodic.annual[0].key
          : appData.metaTerms.ranking.annual[0].key
        )
      }

      if (isNull(newTerm)) {
        log.error(
          "Cannot change chart because no term found for:{",
          newChart.isPeriodic(),
          clone.#term.interval,
          clone.#term.key,
          "}"
        )
        return
      }
      // all set, change the #chart before calling #updateTerm() to avoid
      // a loop, because #updateTerm() also calls #updateChart() if there
      // is a mismatch in isPeriodic
      clone.#chart = newChart

      eventTracker.trackEvent({
        event: "chart:change_global_data",
        data: {
          oldData: this.globalDataChangeToString(),
          newData: clone.globalDataChangeToString(),
        },
      })

      this.#updateTerm(clone, newTerm!)
    }
  }

  // check if new comparison is a custom list and different from previous comparison
  // then load custom list metrics to finally update comparison
  #updateComparison(clone: GlobalData, newComparison: ComparisonNew) {
    const oldComparison = clone.#comparison

    if (
      newComparison.isCustomList() &&
      newComparison.getGroup() !== oldComparison.getGroup()
    ) {
      clone.#entityValues = null
      clone.#loadEntityValues().then(() => (clone.#comparison = newComparison))
    }

    clone.#comparison = newComparison
  }

  async #loadEntityCustomLists() {
    const isFund = this.#entity.isFund()
    const entityId = this.#entity.getId()
    const interval = this.#term.interval

    if (isFund) {
      apiv2_getFundCustomLists(entityId).then((data) => {
        if (data?.fundsets?.custom.length > 0) {
          const customLists = data.fundsets?.custom.map((customList) => {
            if (this.#comparison.getGroup() === customList.key) {
              this.#loadCustomListMetrics(customList.key, interval)
            }

            return {
              key: customList.key,
              name: `${customList.name}${customList.status === "pending" ? "*" : ""}`,
              isGlobal: customList.isForAll,
            }
          })
          this.#comparison.setCustomGroups(customLists)
        } else {
          this.#comparison.setCustomGroups([])
        }
      })
    } else {
      apiv2_getFirmCustomLists(entityId).then((data) => {
        if (data?.firmsets?.custom.length > 0) {
          const customLists = data.firmsets?.custom.map((customList) => {
            if (this.#comparison.getGroup() === customList.key) {
              this.#loadCustomListMetrics(customList.key, interval)
            }

            return {
              key: customList.key,
              name: `${customList.name}${customList.status === "pending" ? "*" : ""}`,
              isGlobal: customList.isForAll,
            }
          })
          this.#comparison.setCustomGroups(customLists)
        } else {
          this.#comparison.setCustomGroups([])
        }
      })
    }
  }

  // reload entityValues async
  async #loadEntityValues() {
    this.#entityValues = null // sets the status to Loading

    log.debug("Loading entity values")
    const filter = this.#filter

    const id = this.#entity.getId()
    const interval = this.#term.interval

    if (this.#entity.isFirm()) {
      apiv2_getFirmInterval(id, interval, filter.key)
        .then((data) => {
          log.debug("Loaded entity values", data)

          this.#entityValues = data
        })
        .catch((error) => {
          log.error("Error loading loadEntityValues", error)
          this.#error = {
            hasError: true,
            message: `System Error: Error loading data for ${this.#entity.getName()} at ${interval} interval and ${this.#term.name[1]} term with ${this.#chart.name} chart selected, please contact support.`,
          }
        })
    } else {
      apiv2_getFundInterval(id, interval, filter.key)
        .then((data) => {
          this.#entityValues = data
        })
        .catch((error) => {
          log.error("Error loading loadEntityValues", error)
          this.#error = {
            hasError: true,
            message: `System Error: Error loading data for ${this.#entity.getName()} at ${interval} interval and ${this.#term.name[1]} term with ${this.#chart.name} chart selected, please contact support.`,
          }
        })
    }

    await this.#loadEntityCustomLists()
  }

  // reload or reset categoryValues async
  async #loadCategoryValues() {
    this.#categoryValues = null // sets the status to Loading

    const filter = this.#filter

    if (this.#entity.isFirm()) return // nothing else to do for firms
    // for funds, load the category api data
    const categoryId = this.#entity.getFund().categoryId
    const interval = this.#term.interval
    apiv2_getCategoryInterval(categoryId, interval, filter.key)
      .then((data) => {
        this.#categoryValues = data
      })
      .catch((error) => {
        log.error("Error loading loadCategoryValues", error)
        this.#error = {
          hasError: true,
          message: `System Error: Error loading data for ${this.#entity.getName()}'s category ${categoryId} at ${interval} interval and ${this.#term.name[1]} term with ${this.#chart.name} chart selected, please contact support.`,
        }
      })
  }

  async #loadCustomListMetrics(listKey: string, interval: string) {
    const isFund = this.#entity.isFund()

    const filter = this.#filter

    const customMetrics = await getCustomListMetrics(
      listKey,
      interval,
      filter.key
    )

    const newMetrics =
      customMetrics?.[isFund ? "fundsets" : "firmsets"]?.custom[0]

    if (newMetrics && this.#entityValues) {
      if (isFund) {
        this.#entityValues.fundsets!.customMetrics = newMetrics
      } else {
        this.#entityValues.firmsets!.customMetrics = newMetrics
      }
    } else {
      this.#error = {
        hasError: true,
        message: `Failed to load custom metrics for List Key ${listKey} in interval ${interval}.`,
      }
    }
  }

  #loadDefaultSettings(appData: AppData) {
    log.debug("Loading Global Data from system defaults")

    const entity =
      Entity.isFundString(INITIAL_ENTITY.entityType) ?
        appData.lookup.getFund(INITIAL_ENTITY.id)
      : appData.lookup.getFirm(INITIAL_ENTITY.id)

    if (!entity) return

    this.#entity = new Entity(entity)
    this.#chart = INITIAL_CHART
    this.#comparison = INITIAL_COMPARISON

    const term = appData.lookup.getTerm(
      this.#chart.isPeriodic(),
      INITIAL_TERM.interval,
      INITIAL_TERM.key
    )

    if (!term) return
    this.#term = term
  }

  #toJson(): string {
    const entity = {
      refId: this.#entity.get().refId,
      type: this.#entity.propertyName(),
    }
    const term = {
      key: this.#term.key,
      interval: this.#term.interval,
    }
    const comparison = {
      group: this.#comparison.getGroup(),
      calc: this.#comparison.getCalculation(),
    }
    const chart = this.getChart().code

    return JSON.stringify({ entity, term, comparison, chart })
  }

  #fromJson(json: string, appData: AppData): GlobalData {
    const parsedJson = JSON.parse(json)
    log.debug("GlobalData.toJson", parsedJson)

    const entity =
      Entity.isFundString(parsedJson.entity.type) ?
        appData.lookup.getFundByRef(parsedJson.entity.refId)
      : appData.lookup.getFirmByRef(parsedJson.entity.refId)

    if (entity) this.#entity = new Entity(entity)
    this.#chart = findChart(parsedJson.chart)

    this.#comparison = ComparisonNew.getComparisonParameters(
      parsedJson.comparison.group,
      parsedJson.comparison.calc
    )

    this.#term =
      appData.lookup.getTerm(
        this.#chart.isPeriodic(),
        parsedJson.term.interval,
        parsedJson.term.key
      ) ?? this.getTerm()

    log.debug("Loaded GlobalData from Session Storage:", this.#toJson())
    return this
  }

  #removeLocalStorageGlobalMetrics() {
    let prefixes = [
      "getFundInterval(",
      "getCategoryInterval(",
      "getFirmInterval(",
      "getFund(",
      "getFirm(",
      "customListMetrics(",
      "getFirmCustomLists(",
      "getFundCustomLists(",
    ]

    const allKeys = LocalStorage.findAllKeys()

    if (!allKeys) return

    prefixes.forEach((prefix) => {
      allKeys.forEach((key) => {
        if (key.startsWith(prefix)) {
          log.debug("Removing localStorage key:", key)
          LocalStorage.remove(key)
        }
      })
    })
  }

  #removeLocalStorageIndustryMetrics() {
    const allKeys = LocalStorage.findAllKeys()

    if (!allKeys) return

    allKeys.forEach((key) => {
      if (key.startsWith("getIndustryInterval")) {
        log.debug("Removing localStorage key:", key)
        LocalStorage.remove(key)
      }
    })
  }

  async reloadCustomListData() {
    await this.#loadEntityCustomLists()
  }
}
